import React, { useState } from 'react';
import styled from 'styled-components';
import type { PrefixInfo } from '../../utils/studentIdPrefixUtils';
import type { StudentIdPrefixFilter } from '../../types';

// ─── Styled Components ───────────────────────────────────────────────

const Panel = styled.div<{ $expanded: boolean }>`
  border: 1px solid ${p => p.$expanded ? '#818cf8' : '#e2e8f0'};
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s ease;
  background: ${p => p.$expanded ? '#f5f3ff' : '#f8fafc'};
`;

const ToggleButton = styled.button`
  width: 100%;
  padding: 10px 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  &:hover { background: #ede9fe; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ToggleLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActiveBadge = styled.span`
  background: #6366f1;
  color: white;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
  font-weight: 500;
`;

const Content = styled.div`
  padding: 12px;
  border-top: 1px solid #e2e8f0;
`;

const CheckboxGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`;

const PrefixCheckbox = styled.label<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  border: 1px solid ${p => p.$checked ? '#6366f1' : '#e2e8f0'};
  background: ${p => p.$checked ? '#e0e7ff' : '#f8fafc'};
  color: ${p => p.$checked ? '#4338ca' : '#475569'};
  transition: all 0.15s ease;
  &:hover { border-color: #818cf8; background: #ede9fe; }
  input { cursor: pointer; accent-color: #6366f1; }
`;

const CountBadge = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 400;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const SmallButton = styled.button`
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #e2e8f0;
  color: #475569;
  transition: all 0.15s;
  &:hover { background: #cbd5e1; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Summary = styled.div`
  font-size: 12px;
  color: #6366f1;
  font-weight: 500;
  margin-top: 8px;
`;

// ─── Props ────────────────────────────────────────────────────────────

export interface PrefixFilterPanelProps {
  availablePrefixes: PrefixInfo[];
  value: StudentIdPrefixFilter | undefined;
  onChange: (filter: StudentIdPrefixFilter | undefined) => void;
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  /** Whether sinif+variant+fenn are all selected. When false, shows a guidance message instead of checkboxes. */
  fieldsSelected?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────

const PrefixFilterPanel: React.FC<PrefixFilterPanelProps> = ({
  availablePrefixes,
  value,
  onChange,
  loading = false,
  disabled = false,
  label = 'Ətraflı filtr (iş nömrəsi prefiksi)',
  fieldsSelected = true,
}) => {
  const [expanded, setExpanded] = useState(!!value?.enabled);
  const selectedSet = new Set(value?.prefixes || []);

  const totalSelected = availablePrefixes
    .filter(p => selectedSet.has(p.prefix))
    .reduce((sum, p) => sum + p.count, 0);
  const totalAll = availablePrefixes.reduce((sum, p) => sum + p.count, 0);

  const handleTogglePrefix = (prefix: string) => {
    if (disabled) return;
    const newSet = new Set(selectedSet);
    if (newSet.has(prefix)) {
      newSet.delete(prefix);
    } else {
      newSet.add(prefix);
    }
    const newPrefixes = Array.from(newSet).sort();
    onChange(newPrefixes.length > 0
      ? { enabled: true, prefixes: newPrefixes }
      : undefined
    );
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onChange({ enabled: true, prefixes: availablePrefixes.map(p => p.prefix) });
  };

  const handleClear = () => {
    if (disabled) return;
    onChange(undefined);
  };

  const handleToggleExpanded = () => {
    if (disabled) return;
    if (expanded) {
      // Collapsing — clear the filter
      setExpanded(false);
      onChange(undefined);
    } else {
      setExpanded(true);
    }
  };

  const activeCount = selectedSet.size;

  return (
    <Panel $expanded={expanded}>
      <ToggleButton onClick={handleToggleExpanded} disabled={disabled}>
        <ToggleLabel>
          {label}
          {activeCount > 0 && !expanded && (
            <ActiveBadge>{activeCount} prefiks</ActiveBadge>
          )}
        </ToggleLabel>
        <span style={{ fontSize: '14px', color: '#94a3b8' }}>{expanded ? '▲' : '▼'}</span>
      </ToggleButton>
      {expanded && (
        <Content>
          {!fieldsSelected ? (
            <div style={{ textAlign: 'center', padding: '10px 12px', color: '#94a3b8', fontSize: '13px' }}>
              Prefiksləri görmək üçün sinif, variant və fənn seçin
            </div>
          ) : loading ? (
            <div style={{ textAlign: 'center', padding: '12px', color: '#94a3b8', fontSize: '13px' }}>
              Prefikslər yüklənir...
            </div>
          ) : availablePrefixes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '12px', color: '#94a3b8', fontSize: '13px' }}>
              Bu filtr üçün şagird tapılmadı
            </div>
          ) : (
            <>
              <CheckboxGrid>
                {availablePrefixes.map(({ prefix, count }) => (
                  <PrefixCheckbox key={prefix} $checked={selectedSet.has(prefix)}>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(prefix)}
                      onChange={() => handleTogglePrefix(prefix)}
                      disabled={disabled}
                    />
                    {prefix} <CountBadge>({count})</CountBadge>
                  </PrefixCheckbox>
                ))}
              </CheckboxGrid>
              <ButtonRow>
                <SmallButton onClick={handleSelectAll} disabled={disabled}>Hamısını seç</SmallButton>
                <SmallButton onClick={handleClear} disabled={disabled || activeCount === 0}>Filtri sil</SmallButton>
              </ButtonRow>
              {activeCount > 0 && (
                <Summary>
                  Seçilmiş: {activeCount} prefiks, {totalSelected}/{totalAll} şagird
                </Summary>
              )}
            </>
          )}
        </Content>
      )}
    </Panel>
  );
};

export default PrefixFilterPanel;
